// frontend/src/presentation/hooks/usePttVoiceCapture.ts
//
// Sesja nagrywania PTT w TRYBIE TOGGLE z komunikatami głosowymi i watchdogiem
// ciszy — „bulletproof" tor, którym steruje natywny mostek:
//
//   start()  → TTS „Mów" (z BEZPIECZNIKIEM Promise.race 1200 ms, żeby zawieszony
//              silnik TTS na Androidzie nie zablokował mikrofonu)
//            → getUserMedia + (opcjonalnie) MediaRecorder
//            → VoiceActivityDetector (cisza 10 s → auto-stop)
//   stop()   → domknięcie nagrania (ręczne)
//   cisza 10 s → domknięcie nagrania + TTS „Koniec nagrywania" + onSilenceReset()
//              (spina się z `usePttManager().resetAllSources`, żeby zsynchronizo-
//               wać sprzętowy przełącznik w mostku)
//
// Cała rozmowa z API przeglądarki (`getUserMedia`, `MediaRecorder`,
// `AudioContext`) jest wstrzykiwalna — w testach atrapy, w produkcji realne API.

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  VoiceActivityDetector,
  type VoiceActivityDetectorOptions,
} from '../../infrastructure/audio/VoiceActivityDetector'
import {
  speechSynthesisService,
  type SpeechSynthesisService,
} from '../services/SpeechSynthesisService'

/** Powód domknięcia nagrania: `'manual'` = stop()/puszczenie, `'silence'` = watchdog VAD. */
export type PttCaptureEndReason = 'manual' | 'silence'

/** Minimalny kontrakt lektora, jakiego potrzebuje ten hook. */
export type PttVoiceCaptureSpeech = Pick<SpeechSynthesisService, 'speak' | 'speakAndWait'>

/** Wstrzykiwalne zależności — domyślnie realne API przeglądarki / współdzielony lektor. */
export interface PttVoiceCaptureDeps {
  speech?: PttVoiceCaptureSpeech
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>
  createRecorder?: (stream: MediaStream) => MediaRecorder
  createVad?: (options: VoiceActivityDetectorOptions) => VoiceActivityDetector
}

export interface UsePttVoiceCaptureOptions {
  /**
   * Wołane po AUTO-STOPIE z ciszy (VAD) — spina się z
   * `usePttManager().resetAllSources`, żeby zsynchronizować mostek.
   */
  onSilenceReset?: () => void
  /** Wołane, gdy komunikat „Mów" ucichł i można ruszyć z nagraniem (np. `dictation.start`). */
  onBeginRecording?: () => void
  /** Wołane przy KAŻDYM domknięciu nagrania (ręcznym i z ciszy) — np. `dictation.stop`. */
  onEndRecording?: (reason: PttCaptureEndReason) => void
  /** Gotowy blob z własnego `MediaRecorder` (tylko gdy `captureBlob !== false`). */
  onCapture?: (blob: Blob) => void
  /** Komunikat TTS na starcie. Domyślnie „Mów". */
  startPrompt?: string
  /** Komunikat TTS po auto-stopie z ciszy. Domyślnie „Koniec nagrywania". */
  endPrompt?: string
  /** Limit bezpiecznika TTS (`Promise.race`). Domyślnie 1200 ms. */
  ttsTimeoutMs?: number
  /** Długość ciągłej ciszy do auto-stopu. Domyślnie 10 000 ms. */
  silenceMs?: number
  /** Czy prowadzić WŁASNY `MediaRecorder` (blob → `onCapture`). Domyślnie `true`. */
  captureBlob?: boolean
  deps?: PttVoiceCaptureDeps
}

export interface PttVoiceCaptureController {
  /** Rozpoczyna sesję: „Mów" → nagrywanie → watchdog ciszy. Idempotentne. */
  start: () => Promise<void>
  /** Domyka sesję ręcznie (odpowiednik puszczenia / drugiego kliknięcia toggle). */
  stop: () => void
  /** `true`, dopóki sesja jest w toku (od „Mów" do domknięcia). */
  isCapturing: boolean
}

const DEFAULT_START_PROMPT = 'Mów'
const DEFAULT_END_PROMPT = 'Koniec nagrywania'

type SessionState = 'idle' | 'starting' | 'recording' | 'finishing'

/**
 * Orkiestruje sesję nagrywania PTT w trybie Toggle: bezpieczny start z TTS,
 * własny `MediaRecorder` (opcjonalny) i detekcję ciszy z resetem mostka.
 */
export function usePttVoiceCapture(
  options: UsePttVoiceCaptureOptions = {},
): PttVoiceCaptureController {
  const speech: PttVoiceCaptureSpeech = options.deps?.speech ?? speechSynthesisService
  const getUserMedia =
    options.deps?.getUserMedia ??
    ((constraints: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(constraints))
  const createRecorder =
    options.deps?.createRecorder ?? ((stream: MediaStream) => new MediaRecorder(stream))
  const createVad =
    options.deps?.createVad ??
    ((vadOptions: VoiceActivityDetectorOptions) => new VoiceActivityDetector(vadOptions))

  // Świeże referencje — sesja żyje w callbackach montowanych raz, re-render
  // rodzica (Screen1 renderuje się bardzo często) nie może nic zrywać.
  const optionsRef = useRef(options)
  optionsRef.current = options
  const speechRef = useRef(speech)
  speechRef.current = speech
  const getUserMediaRef = useRef(getUserMedia)
  getUserMediaRef.current = getUserMedia
  const createRecorderRef = useRef(createRecorder)
  createRecorderRef.current = createRecorder
  const createVadRef = useRef(createVad)
  createVadRef.current = createVad

  const stateRef = useRef<SessionState>('idle')
  const [isCapturing, setIsCapturing] = useState(false)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const vadRef = useRef<VoiceActivityDetector | null>(null)
  const chunksRef = useRef<Blob[]>([])

  const goIdle = useCallback((): void => {
    stateRef.current = 'idle'
    setIsCapturing(false)
  }, [])

  const finish = useCallback(
    (reason: PttCaptureEndReason): void => {
      const prev = stateRef.current
      if (prev === 'idle' || prev === 'finishing') return

      // stop() w trakcie komunikatu „Mów" — nie ma jeszcze czego domykać,
      // ale trzeba przerwać rozpoczynanie (patrz strażnik w `start`).
      if (prev === 'starting') {
        goIdle()
        return
      }

      stateRef.current = 'finishing'

      vadRef.current?.stop()
      vadRef.current = null

      const recorder = recorderRef.current
      recorderRef.current = null
      const stream = streamRef.current
      streamRef.current = null

      const afterRecorder = (): void => {
        if (stream) for (const track of stream.getTracks()) track.stop()
        optionsRef.current.onEndRecording?.(reason)
        if (reason === 'silence') {
          speechRef.current.speak(optionsRef.current.endPrompt ?? DEFAULT_END_PROMPT)
          optionsRef.current.onSilenceReset?.()
        }
        goIdle()
      }

      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = () => {
          const chunks = chunksRef.current
          chunksRef.current = []
          if (chunks.length > 0) {
            optionsRef.current.onCapture?.(
              new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }),
            )
          }
          afterRecorder()
        }
        try {
          recorder.stop()
        } catch {
          afterRecorder()
        }
      } else {
        afterRecorder()
      }
    },
    [goIdle],
  )

  const start = useCallback(async (): Promise<void> => {
    if (stateRef.current !== 'idle') return
    stateRef.current = 'starting'
    setIsCapturing(true)

    const opts = optionsRef.current

    // 1. BEZPIECZNY START Z TTS — „Mów". `speakAndWait` sam pilnuje bezpiecznika
    //    (Promise.race z timeoutem), więc nawet zawieszony silnik TTS nie
    //    zablokuje tu nagrywania.
    await speechRef.current.speakAndWait(opts.startPrompt ?? DEFAULT_START_PROMPT, {
      timeoutMs: opts.ttsTimeoutMs,
    })

    // stop() w trakcie „Mów" → `finish` przestawił stan na 'idle'.
    if (stateRef.current !== 'starting') {
      goIdle()
      return
    }

    // 2. Mikrofon.
    let stream: MediaStream
    try {
      stream = await getUserMediaRef.current({ audio: true })
    } catch (error) {
      console.warn('[usePttVoiceCapture] Brak dostępu do mikrofonu — przerywam sesję.', error)
      goIdle()
      return
    }

    if (stateRef.current !== 'starting') {
      for (const track of stream.getTracks()) track.stop()
      goIdle()
      return
    }

    streamRef.current = stream
    stateRef.current = 'recording'
    optionsRef.current.onBeginRecording?.()

    // 3. Własny MediaRecorder (opcjonalny) — surowe audio do `onCapture`.
    if (optionsRef.current.captureBlob !== false) {
      const recorder = createRecorderRef.current(stream)
      recorderRef.current = recorder
      chunksRef.current = []
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.start()
    }

    // 4. Watchdog ciszy — cisza `silenceMs` → domknij + zresetuj mostek.
    const vad = createVadRef.current({
      stream,
      silenceMs: optionsRef.current.silenceMs,
      onSilence: () => finish('silence'),
    })
    vadRef.current = vad
    vad.start()
  }, [finish, goIdle])

  const stop = useCallback((): void => {
    finish('manual')
  }, [finish])

  // Odmontowanie w trakcie sesji — domknij zasoby (bez komunikatu / resetu).
  useEffect(() => {
    return () => {
      if (stateRef.current !== 'idle') finish('manual')
    }
  }, [finish])

  return { start, stop, isCapturing }
}
